import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Grid,
  Divider,
  Button,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Casino as CasinoIcon,
} from '@mui/icons-material';
import { usePostHog } from 'posthog-js/react';
import {
  loadSearchData,
  search,
  SearchResult,
  getRandomCysteine,
  getGeneNameByProteinId,
} from '../utils/searchDataLoader';
import { getCellLineDataAsync } from '../data/cellLineData/loader';
import ProteinViewer from './ProteinViewer';
import SequenceViewer from './SequenceViewer';
import { Tabs, Tab } from '@mui/material';
import confetti from 'canvas-confetti';

interface CysteineData {
  identifier: string;
  altIdentifier: string;
  modifiedPeptide: string;
  log2Average: number;
  isHyperReactive: boolean;
}

interface CellLineData {
  cellLine: string;
  tissue: string;
  cysteines: Record<string, CysteineData>;
}

type SearchSource = 'search_submit' | 'suggestion_click' | 'feeling_lucky';

const ProteinExplorer: React.FC = () => {
  const posthog = usePostHog();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedProtein, setSelectedProtein] = useState<string | null>(null);
  const [geneName, setGeneName] = useState<string | null>(null);
  const [selectedCysteine, setSelectedCysteine] = useState<string | null>(null);
  const [cellLineData, setCellLineData] = useState<CellLineData[]>([]);
  const [cysteineColumns, setCysteineColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [observedCysteines, setObservedCysteines] = useState<string[]>([]);
  const [hyperreactiveCysteines, setHyperreactiveCysteines] = useState<string[]>([]);
  const [rightPanelTab, setRightPanelTab] = useState<number>(0); // 0: Sequence, 1: Structure
  const [showCake, setShowCake] = useState(false);
  const [candlesLit, setCandlesLit] = useState(false);

  // Track cysteine selection in PostHog
  const trackCysteineSelection = (
    source: SearchSource,
    result: SearchResult,
    inputQuery?: string
  ) => {
    if (!posthog) return;
    const searchQuery = (inputQuery ?? query).trim();
    const sessionURL = posthog.get_session_replay_url?.({ withTimestamp: true });
    posthog.capture('cysteine_search', {
      source,
      query: searchQuery || undefined,
      result_type: result.type,
      result_primary: result.primary,
      result_secondary: result.secondary,
      result_tertiary: result.tertiary,
      result_index: result.index,
      is_feeling_lucky: source === 'feeling_lucky',
      sessionURL,
    });
  };

  // Load search data on mount
  useEffect(() => {
    loadSearchData()
      .then(() => setDataLoaded(true))
      .catch((err) => console.error('Failed to load search data:', err));
  }, []);

  // Handle search
  useEffect(() => {
    if (query.trim() && dataLoaded) {
      const results = search(query, 'all');
      setSearchResults(results.slice(0, 10)); // Limit to 10 results
    } else {
      setSearchResults([]);
    }
  }, [query, dataLoaded]);

  const handleResultClick = async (result: SearchResult, source: SearchSource = 'suggestion_click') => {
    // Track the search
    trackCysteineSelection(source, result, query);

    let proteinId: string;
    let cysteineToHighlight: string | null = null;

    if (result.type === 'protein') {
      proteinId = result.primary;
    } else {
      // Extract protein from cysteine identifier
      const [protein, cysteine] = result.primary.split('_');
      proteinId = protein;
      cysteineToHighlight = cysteine;
    }

    // If it's the same protein, just update the cysteine highlight
    if (proteinId === selectedProtein) {
      setSelectedCysteine(cysteineToHighlight);
      setQuery('');
      setSearchResults([]);
      return;
    }

    // Set new protein and related data
    setSelectedProtein(proteinId);
    setSelectedCysteine(cysteineToHighlight);
    const gene = getGeneNameByProteinId(proteinId);
    setGeneName(gene);
    setQuery('');
    setSearchResults([]);

    // Load cell line data for this protein
    await loadCellLineData(proteinId);
  };

  const handleFeelingLucky = () => {
    // Show cake and light candles
    setShowCake(true);
    setTimeout(() => setCandlesLit(true), 100);

    // Trigger confetti effect
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        // Reset to dice after confetti completes
        setTimeout(() => {
          setCandlesLit(false);
          setShowCake(false);
        }, 300);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Fire confetti from random positions across the screen
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Get random cysteine and handle selection
    const randomCysteine = getRandomCysteine();
    if (randomCysteine) {
      handleResultClick(randomCysteine, 'feeling_lucky');
    }
  };

  const loadCellLineData = async (proteinId: string) => {
    setLoading(true);
    const cellLines = [
      'Breast_BT-549', 'Breast_Hs-578T', 'Breast_MCF-7', 'Breast_MDA-MB-231',
      'Breast_MDA-MB-468', 'Breast_T-47D', 'CNS_SF-268', 'CNS_SF-295',
      'CNS_SF-539', 'CNS_SNB-19', 'CNS_SNB-75', 'CNS_U251',
      'Colon_COLO-205', 'Colon_HCC-2998', 'Colon_HCT-116', 'Colon_HCT-15',
      'Colon_HT29', 'Colon_SW-620', 'Leukemia_CCRF-CEM', 'Leukemia_HL-60',
      'Leukemia_K562', 'Leukemia_MOLT-4', 'Leukemia_RPMI-8226', 'Leukemia_SR',
      'Melanoma_LOX-IMVI', 'Melanoma_M14', 'Melanoma_MALME-3M', 'Melanoma_MDA-MB-435',
      'Melanoma_SK-MEL-28', 'Melanoma_SK-MEL-2', 'Melanoma_SK-MEL-5', 'Melanoma_UACC-257',
      'Melanoma_UACC-62', 'NSCL_A549', 'NSCL_EKVX', 'NSCL_HOP-62',
      'NSCL_HOP-92', 'NSCL_NCI-H226', 'NSCL_NCI-H23', 'NSCL_NCI-H322M',
      'NSCL_NCI-H460', 'NSCL_NCI-H522', 'Ovarian_ADR-RES', 'Ovarian_IGR-OV1',
      'Ovarian_OVCAR-3', 'Ovarian_OVCAR-4', 'Ovarian_OVCAR-5', 'Ovarian_OVCAR-8',
      'Ovarian_SK-OV-3', 'Prostate_DU145', 'Prostate_PC-3', 'Renal_786-0',
      'Renal_A498', 'Renal_ACHN', 'Renal_CAKI-1', 'Renal_RXF-393',
      'Renal_SN12C', 'Renal_TK-10', 'Renal_UO-31'
    ];

    const allCysteineIds = new Set<string>();
    const observedSet = new Set<string>();
    const hyperSet = new Set<string>();
    const cellLineDataArray: CellLineData[] = [];

    for (const cellLine of cellLines) {
      try {
        const rows = await getCellLineDataAsync(cellLine);
        if (!rows || rows.length === 0) continue;
        
        const [tissue, cellLineName] = cellLine.split('_');
        const cysteineData: Record<string, CysteineData> = {};
        
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i];
          if (!values || values.length === 0) continue;
          
          const identifier = values[0];
          const altIdentifier = values[1];
          const [currentProtein] = identifier.split('_');
          
          if (currentProtein !== proteinId) continue;
          
          const cysteineId = identifier.split('_')[1];
          allCysteineIds.add(cysteineId);
          observedSet.add(cysteineId);
          if (values[4] === 'True') {
            hyperSet.add(cysteineId);
          }
          
          cysteineData[cysteineId] = {
            identifier,
            altIdentifier,
            modifiedPeptide: values[2],
            log2Average: parseFloat(values[3]) || 0,
            isHyperReactive: values[4] === 'True'
          };
        }
        
        if (Object.keys(cysteineData).length > 0) {
          cellLineDataArray.push({
            cellLine: cellLineName,
            tissue,
            cysteines: cysteineData
          });
        }
      } catch (error) {
        console.error(`Error loading data for ${cellLine}:`, error);
      }
    }
    
    const sortedCysteines = Array.from(allCysteineIds).sort((a, b) => {
      const numA = parseInt(a.substring(1));
      const numB = parseInt(b.substring(1));
      return numA - numB;
    });
    
    setCysteineColumns(sortedCysteines);
    setCellLineData(cellLineDataArray);
    setObservedCysteines(Array.from(observedSet).sort((a, b) => parseInt(a.substring(1)) - parseInt(b.substring(1))));
    setHyperreactiveCysteines(Array.from(hyperSet).sort((a, b) => parseInt(a.substring(1)) - parseInt(b.substring(1))));
    setLoading(false);
  };

  const getCellColor = (value: number, isHyperReactive: boolean) => {
    if (isHyperReactive) {
      return '#ff6b6b';
    }
    
    const min = -2;
    const max = 8;
    const normalized = (value - min) / (max - min);
    
    if (normalized < 0.5) {
      const intensity = normalized * 2;
      return `rgb(${Math.floor(255 * intensity)}, ${Math.floor(255 * intensity)}, 255)`;
    } else {
      const intensity = (normalized - 0.5) * 2;
      return `rgb(255, 255, ${Math.floor(255 * (1 - intensity))})`;
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column' }}>
      {/* Search Bar */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          borderRadius: 0,
          borderBottom: '2px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative' }}>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for proteins (e.g., P04637, TP53) or cysteines (e.g., P04637_C135)"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                '& fieldset': {
                  borderWidth: 2,
                },
              },
            }}
          />
          
          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={
                showCake ? (
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      '@keyframes flameFlicker': {
                        '0%, 100%': { opacity: 0.85 },
                        '50%': { opacity: 1 },
                      },
                      '@keyframes flameDance': {
                        '0%, 100%': { transform: 'translateY(-1px) scale(1)' },
                        '50%': { transform: 'translateY(1px) scale(1.05)' },
                      },
                      '@keyframes flameSway': {
                        '0%, 100%': { transform: 'translateX(-0.5px)' },
                        '50%': { transform: 'translateX(0.5px)' },
                      },
                    }}
                  >
                    {/* Cake base */}
                    <Box sx={{ fontSize: '1.1rem', lineHeight: 1, zIndex: 1 }}>🎂</Box>

                    {/* Flames container */}
                    {candlesLit && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          gap: '3px',
                          flexWrap: 'wrap',
                          width: '20px',
                          justifyContent: 'center',
                        }}
                      >
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <Box
                            key={i}
                            sx={{
                              width: '3px',
                              height: '6px',
                              background: 'linear-gradient(to top, #ff4500, #ff8c00, #ffd700, rgba(255,255,255,0.9))',
                              borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                              animation: 'flameFlicker 0.15s ease-in-out infinite, flameDance 0.3s ease-in-out infinite, flameSway 0.4s ease-in-out infinite',
                              animationDelay: `${i * 0.08}s, ${i * 0.05}s, ${i * 0.1}s`,
                              boxShadow: '0 0 3px rgba(255, 140, 0, 0.6)',
                              willChange: 'transform, opacity',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <CasinoIcon />
                )
              }
              onClick={handleFeelingLucky}
              size="small"
              sx={{
                transition: 'transform 0.2s ease-out',
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              I'm Feeling Lucky
            </Button>
          </Box>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                zIndex: 1000,
                borderRadius: 2,
              }}
            >
              <List disablePadding>
                {searchResults.map((result, index) => (
                  <React.Fragment key={`${result.type}-${result.primary}-${index}`}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleResultClick(result)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={result.type === 'cysteine' ? 'C' : 'P'}
                              size="small"
                              color={result.type === 'cysteine' ? 'warning' : 'info'}
                              sx={{ minWidth: 28 }}
                            />
                            <Typography variant="body1" fontWeight={500}>
                              {result.primary}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {result.secondary}
                            </Typography>
                            {result.tertiary && (
                              <Typography variant="caption" color="warning.main">
                                {result.tertiary}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      {selectedProtein ? (
        <Fade in={true}>
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <Grid container sx={{ height: '100%' }}>
              {/* Left Panel - Cysteine Data */}
              <Grid item xs={12} md={7} sx={{ height: '100%', overflow: 'hidden' }}>
                <Paper 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 0,
                    borderRight: { md: '1px solid' },
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(to right, #2563eb, #3b82f6)',
                    color: 'white',
                  }}>
                    <Typography variant="h5" fontWeight={600}>
                      Cysteine Data: {geneName ? `${geneName} (${selectedProtein})` : selectedProtein}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                      {cysteineColumns.length} cysteine sites across {cellLineData.length} cell lines
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {loading ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography>Loading cysteine data...</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        position: 'relative',
                        maxHeight: 'calc(100% - 20px)',
                        overflow: 'auto',
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ 
                                position: 'sticky', 
                                top: 0,
                                left: 0, 
                                background: '#f5f7fa',
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #e0e0e0',
                                minWidth: 120,
                                zIndex: 3,
                                boxShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                              }}>
                                Cell Line
                              </th>
                              {cysteineColumns.map(cys => (
                                <th 
                                  key={cys} 
                                  style={{ 
                                    position: 'sticky',
                                    top: 0,
                                    padding: '12px 8px',
                                    textAlign: 'center',
                                    borderBottom: '2px solid #e0e0e0',
                                    cursor: 'pointer',
                                    background: selectedCysteine === cys ? '#e3f2fd' : '#f5f7fa',
                                    fontWeight: selectedCysteine === cys ? 700 : 600,
                                    zIndex: 2,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  }}
                                  onClick={() => setSelectedCysteine(cys)}
                                >
                                  {cys}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {cellLineData.map(({ cellLine, tissue, cysteines }) => (
                              <tr key={cellLine}>
                                <td style={{ 
                                  position: 'sticky', 
                                  left: 0, 
                                  background: 'white',
                                  padding: '12px',
                                  borderBottom: '1px solid #f0f0f0',
                                  boxShadow: '2px 0 4px rgba(0,0,0,0.05)',
                                  zIndex: 1,
                                }}>
                                  <div style={{ fontWeight: 600 }}>{cellLine}</div>
                                  <div style={{ fontSize: '0.85rem', color: '#666' }}>{tissue}</div>
                                </td>
                                {cysteineColumns.map(cys => {
                                  const data = cysteines[cys];
                                  if (!data) {
                                    return (
                                      <td 
                                        key={cys} 
                                        style={{ 
                                          padding: '8px',
                                          textAlign: 'center',
                                          borderBottom: '1px solid #f0f0f0',
                                          color: '#ccc',
                                        }}
                                      >
                                        -
                                      </td>
                                    );
                                  }
                                  return (
                                    <td
                                      key={cys}
                                      style={{
                                        padding: '8px',
                                        textAlign: 'center',
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor: getCellColor(data.log2Average, data.isHyperReactive),
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        position: 'relative',
                                        border: selectedCysteine === cys ? '2px solid #1976d2' : undefined,
                                      }}
                                      onClick={() => setSelectedCysteine(cys)}
                                      title={`${data.altIdentifier}\nLog2 Average: ${data.log2Average.toFixed(2)}\n${data.isHyperReactive ? 'Hyper-reactive' : ''}`}
                                    >
                                      {data.log2Average.toFixed(1)}
                                      {data.isHyperReactive && (
                                        <span style={{
                                          position: 'absolute',
                                          top: 2,
                                          right: 2,
                                          fontSize: '0.7rem',
                                          color: 'white',
                                          textShadow: '0 0 2px rgba(0,0,0,0.5)',
                                        }}>
                                          ★
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Legend */}
                  <Box sx={{ 
                    p: 2, 
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    background: '#f5f7fa',
                  }}>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                          <Typography variant="caption">Hyper-reactive</Typography>
                        </Box>
                      </Grid>
                      <Grid item>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 60, 
                            height: 20, 
                            background: 'linear-gradient(to right, #0000ff, #ffffff, #ffff00)',
                            borderRadius: 1,
                          }} />
                          <Typography variant="caption">Log2 Ratio Scale</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              {/* Right Panel - Sequence / Structure Tabs */}
              <Grid item xs={12} md={5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Paper sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tabs value={rightPanelTab} onChange={(_, v) => setRightPanelTab(v)} aria-label="sequence-structure-tabs">
                    <Tab label="Sequence" />
                    <Tab label="Structure" />
                  </Tabs>
                </Paper>
                <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
                  {rightPanelTab === 0 ? (
                    <SequenceViewer
                      proteinId={selectedProtein}
                      highlightCysteine={selectedCysteine || undefined}
                      observedCysteines={observedCysteines}
                      hyperreactiveCysteines={hyperreactiveCysteines}
                      onCysteineClick={(cys) => setSelectedCysteine(cys)}
                    />
                  ) : (
                    <ProteinViewer
                      key={selectedProtein}
                      proteinId={selectedProtein}
                      highlightCysteine={selectedCysteine || undefined}
                      onCysteineClick={(cys) => setSelectedCysteine(cys)}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      ) : (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}>
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600 }}>
            <Typography variant="h4" gutterBottom color="primary">
              Welcome to Protein Explorer
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Search for a protein or cysteine to explore detailed cysteine modification data
              across cancer cell lines with interactive 3D protein structure visualization.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try searching for: P04637, TP53, or click "I'm Feeling Lucky"
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ProteinExplorer;
